import { Col, Row, Typography, Radio, Select } from 'antd';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectField } from 'src/components/form';
import useProductSearch from 'src/queries/useProductSearch';
import {
  checkIsProductFinished,
  getProductName,
  normalizeParenthesis,
} from 'src/utils/helper';
import useForm, { FormValue } from './useForm';
import { BLANK_OPTION, CATEGORY_TYPE } from 'src/constants/app';
import Tooltip from 'antd/es/tooltip';

type Props = {
  onClose: () => void;
  onSelectProduct: (productName: string, productId: string) => void;
  productIdSelected?: string;
  productNameInput?: string;
};

type SelectType = 'SELECT' | 'RADIO';

const ProductModal: React.FC<Props> = ({
  onClose,
  onSelectProduct,
  productIdSelected,
  productNameInput,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue, getValues } = useForm();
  const [product, setProduct] = useState<string>('');
  const [isFirst, setIsFirst] = useState(true);

  const {
    dataSubCategories,
    dataProduct,
    trimNameCategories,
    dataProductSearch,
  } = useProductSearch(watch('product_category'), watch('product_shape'));

  const categoriesData = useMemo(
    () =>
      dataProductSearch &&
      dataProductSearch.map((item) => ({
        label: item.categoryName || '',
        value: item.categoryId || '',
        childrenList: item.childrenList,
      })),
    [dataProductSearch?.length, JSON.stringify(dataProductSearch)],
  );

  const varietyValue = watch('product_category') ?? '';

  const subCategories = categoriesData
    ?.find((item) => item.value === varietyValue)
    ?.childrenList.map((item) => ({
      label: item.categoryName,
      value: item.categoryId,
    }));

  useEffect(() => {
    if (
      isFirst &&
      productNameInput &&
      productIdSelected &&
      dataProduct?.length
    ) {
      const productInfo = dataProduct.find(
        (item) => item?.productId === productIdSelected,
      );
      if (productInfo) {
        setProduct(productInfo.productId);
        setValue('product_category', productInfo.hinshL3Cd);
        setValue('product_shape', productInfo.keijoL3Cd);
        setIsFirst(false);
      }
    }
    if (
      isFirst &&
      (!productNameInput || !productIdSelected) &&
      dataSubCategories?.length
    ) {
      setValue('product_category', trimNameCategories[0].value);
      setValue('product_shape', dataSubCategories[0].value);
      setIsFirst(false);
    }
  }, [
    isFirst,
    productIdSelected,
    dataProduct,
    dataSubCategories,
    setValue,
    varietyValue,
    productNameInput,
  ]);

  const handleProductSelection = (selected, type: SelectType) => {
    const selectedProductId =
      type === 'SELECT' ? selected : selected.target?.value;
    setProduct(selectedProductId);
    const selectedProduct = dataProduct?.find(
      (item) => item.productId === selectedProductId,
    );

    if (selectedProduct) {
      setValue('product_category', selectedProduct.hinshL3Cd);
      setValue('product_shape', selectedProduct.keijoL3Cd);
      onSelectProduct(
        getProductName(selectedProduct, true),
        selectedProduct.productId,
      );
      onClose();
    }
  };

  const handleOnSelect = (value: string, type: string) => {
    if (type === CATEGORY_TYPE.METERIAL) {
      const categoriesDataSelected =
        categoriesData?.find((item) => item.value === value)?.childrenList ??
        [];
      const isShapeValid = categoriesDataSelected.some(
        (item) => item.categoryId === getValues('product_shape'),
      );
      if (!isShapeValid) {
        setValue('product_shape', '');
      }
    }
    setProduct('');
  };

  return (
    <div className="p-20 product_search custom_product_search">
      <Row gutter={16}>
        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <SelectField<FormValue>
            label={t('product.category')}
            selectProps={{
              showSearch: true,
              options: [BLANK_OPTION, ...(trimNameCategories || [])],
              filterOption: (input, option) => {
                const label = (option?.label as string) ?? '';
                return label.toLowerCase().includes(input.toLowerCase());
              },
              onSelect: (value) =>
                handleOnSelect(value, CATEGORY_TYPE.METERIAL),
            }}
            controller={{
              name: 'product_category',
              control,
            }}
            labelCol={{ xs: 24, sm: 24, md: 6, lg: 6, xl: 4 }}
            wrapperCol={{ xs: 24, sm: 24, md: 12, lg: 12, xl: 12 }}
            className="product_search_category"
          />
        </Col>

        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <SelectField<FormValue>
            label={
              checkIsProductFinished(watch('product_category'))
                ? t('pricing_page.category_field.type')
                : t('product.shape')
            }
            selectProps={{
              showSearch: true,
              options: [BLANK_OPTION, ...(subCategories || [])],
              filterOption: (input, option) => {
                const label = (option?.label as string) ?? '';
                return label.toLowerCase().includes(input.toLowerCase());
              },
              onSelect: (value) => handleOnSelect(value, CATEGORY_TYPE.SHAPE),
            }}
            controller={{
              name: 'product_shape',
              control,
            }}
            labelCol={{ xs: 24, sm: 24, md: 6, lg: 3, xl: 4 }}
            wrapperCol={{ xs: 24, sm: 24, md: 18, lg: 21, xl: 20 }}
            className="product_search_shape custom_product_search_shape"
          />
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <Row className="category_block_infor_row variety_row" align="middle">
            <Col
              span={4}
              xs={24}
              sm={24}
              md={6}
              lg={6}
              xl={4}
              className="category-label"
            >
              <Typography.Text className="text_category_label">
                {t('pricing_page.product_field')}
              </Typography.Text>
            </Col>
            <Col span={24} xs={24} sm={12} md={12} lg={12} xl={12}>
              <Select
                style={{ width: '100%' }}
                showSearch
                options={
                  dataProduct?.map((item) => ({
                    label: getProductName(item, true),
                    value: item.productId,
                  })) ?? []
                }
                value={product}
                onChange={(val) => handleProductSelection(val, 'SELECT')}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Typography.Text className="product_search_title">
          {t('product.product_name')}
        </Typography.Text>
      </Row>
      <Row className="product_search_list">
        <Col xs={24} sm={24} md={24} xl={24}>
          <Radio.Group
            className="w-full"
            onChange={(e) => handleProductSelection(e, 'RADIO')}
            value={product}
          >
            {dataProduct?.map((item, index) => (
              <Radio
                key={index}
                className="mr-0 mb-24 category_product_item"
                value={item.productId}
              >
                <Tooltip
                  title={normalizeParenthesis(getProductName(item, true))}
                  placement="top"
                  overlayClassName="custom-tooltip"
                >
                  <Typography.Text
                    ellipsis
                    style={{
                      color: item.productId === product ? 'red' : '#000',
                    }}
                  >
                    <div className="product-name_label">
                      <span className="multi_line_ellipsis">
                        {normalizeParenthesis(getProductName(item, true))}
                      </span>
                    </div>
                  </Typography.Text>
                </Tooltip>
              </Radio>
            ))}
          </Radio.Group>
        </Col>
      </Row>
    </div>
  );
};

export default ProductModal;
